import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';

interface BatchMutationOptions {
  /** Additional query keys to invalidate beyond the primary. */
  extraInvalidations?: (cId: number) => readonly unknown[][];
}

/**
 * Factory for batch mutation hooks (activate, deactivate, delete).
 *
 * Eliminates identical boilerplate across videos, playlists, and streams.
 * Each returned hook handles auth context, query invalidation automatically.
 * Notifications are left to the component-level onSuccess callback
 * so each call site can customize the message.
 */
export function createBatchMutationHook<TId = number>(
  apiFn: (cId: number, ids: TId[]) => Promise<void>,
  getQueryKey: (cId: number) => readonly unknown[],
  options?: BatchMutationOptions,
) {
  return function useBatchMutation() {
    const { selectedCId } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (ids: TId[]) => apiFn(selectedCId!, ids),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getQueryKey(selectedCId!) });
        if (options?.extraInvalidations) {
          for (const key of options.extraInvalidations(selectedCId!)) {
            queryClient.invalidateQueries({ queryKey: key });
          }
        }
      },
    });
  };
}
